import { useEffect } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

import {
	SUGGESTIONS_PANEL_OPEN_QUERY_VALUE,
	SUGGESTIONS_PANEL_QUERY_PARAM,
} from './suggestionsPanelDeepLink.ts';
import { useShowSuggestionsPanel } from './useShowSuggestionsPanel.ts';

const useSuggestionsPanelQueryParam = createSingleQueryParamHook(SUGGESTIONS_PANEL_QUERY_PARAM);

/**
 * Shows the Suggestions panel when the editor is loaded with `suggestionsPanel=open`, then removes
 * the parameter so a reload does not show it again. Removing the parameter is what prevents a
 * repeat; there is no per-mount flag because live doc to live doc navigation keeps this hook
 * mounted, and the next doc's deep link must still work. The AI Suggestions plugin loads persisted
 * suggestions itself when the editor view is created, and the panel reconciles them when it
 * mounts, so this hook only needs to show the panel.
 */
export const useShowSuggestionsPanelFromDeepLink = (): void => {
	const suggestionsPanelQueryParam = useSuggestionsPanelQueryParam();
	const { setQueryParams } = useRouteActions();
	const { showSuggestionsPanel } = useShowSuggestionsPanel();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	useEffect(() => {
		// The experiment is checked last so ordinary editor loads are not exposed to it.
		if (
			suggestionsPanelQueryParam !== SUGGESTIONS_PANEL_OPEN_QUERY_VALUE ||
			!fullPageEditorPresetAPI?.aiSuggestions ||
			!expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
		) {
			return;
		}

		showSuggestionsPanel({ entryPoint: 'suggestionsPanel' });
		setQueryParams({ [SUGGESTIONS_PANEL_QUERY_PARAM]: null }, true);
	}, [fullPageEditorPresetAPI, setQueryParams, showSuggestionsPanel, suggestionsPanelQueryParam]);
};
